import React, { SyntheticEvent, useContext, useId, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { OpenStatusDropdownContext } from 'components/tasks/TasksViewer/TaskStatusDropdown';
import ProjectSearchMenu, { ProjectOption } from './ProjectSearchMenu';
import styles from './ProjectSelectorButton.module.css';

interface ProjectToggleProps {
  'aria-expanded'?: boolean;
  className?: string;
  onClick?: (event: SyntheticEvent) => void;
  style?: React.CSSProperties;
}

const ProjectToggle = React.forwardRef<HTMLDivElement, ProjectToggleProps>(({
  'aria-expanded': ariaExpanded,
  className,
  onClick,
  style,
  ...props
}, ref) => {
  const { t } = useTranslation();

  return (
    <div
      {...props}
      aria-expanded={ariaExpanded}
      aria-haspopup="menu"
      aria-label={t('projects')}
      className={`${styles.toggle}${className ? ` ${className}` : ''}`}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      ref={ref}
      role="button"
      style={style}
      tabIndex={0}
      title={t('projects')}
    >
      +
    </div>
  );
});

ProjectToggle.displayName = 'ProjectToggle';

interface ProjectSelectorButtonProps {
  onSelect: (project: ProjectOption) => void;
  projects: ProjectOption[];
  style?: React.CSSProperties;
}

const ProjectSelectorButton = ({
  onSelect,
  projects,
  style,
}: ProjectSelectorButtonProps) => {
  const dropdownId = useId();
  const exclusiveDropdown = useContext(OpenStatusDropdownContext);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = exclusiveDropdown
    ? exclusiveDropdown.openDropdownId === dropdownId
    : internalOpen;

  const setOpen = (nextShow: boolean) => {
    if (exclusiveDropdown) {
      exclusiveDropdown.setOpenDropdownId(nextShow ? dropdownId : null);
      return;
    }
    setInternalOpen(nextShow);
  };

  const selectProject = (project: ProjectOption) => {
    onSelect(project);
    setOpen(false);
  };

  return (
    <Dropdown
      autoClose="outside"
      className={styles.button}
      onToggle={(nextShow: boolean) => setOpen(nextShow)}
      show={isOpen}
    >
      <Dropdown.Toggle
        as={ProjectToggle as React.ElementType}
        style={style}
      />
      <Dropdown.Menu
        data-testid="task-project-menu"
        popperConfig={{
          modifiers: [{ name: 'hide', enabled: false }],
        }}
      >
        <ProjectSearchMenu
          isOpen={isOpen}
          onSelect={selectProject}
          projects={projects}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ProjectSelectorButton;
